import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Grid, Chip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Container
} from '@mui/material';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import axios from 'axios';

import { KPIS_API } from '../apiConfig';
const API_BASE_URL = KPIS_API; // For simplicity in this component

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', maximumFractionDigits: 0 }).format(value);
};

// KPI Card component
function KpiCard({ icon, title, value, subtitle, color, gradient }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background: gradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Box sx={{
        position: 'absolute', top: -15, right: -15,
        width: 80, height: 80, borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.1)'
      }} />
      <Box sx={{
        position: 'absolute', bottom: -25, right: 10,
        width: 100, height: 100, borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.05)'
      }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box sx={{
          bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2,
          p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        bgcolor: 'rgba(255, 255, 255, 0.95)', p: 1.5, borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.05)',
        backdropFilter: 'blur(8px)', zIndex: 1000
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#1e293b' }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color || entry.fill }} />
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
              {entry.name}: <span style={{ fontWeight: 700, color: '#0f172a' }}>{entry.value}</span>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const FORECAST_COLORS = ['#10B981', '#F59E0B', '#EF4444'];

export default function KpiDashboard({ espacios = [] }) {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(API_BASE_URL);
        setKpis(data);
      } catch (err) {
        console.error('Error fetching KPIs:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 5 }}>
        <CircularProgress size={48} />
        <Typography sx={{ ml: 2, color: 'text.secondary' }}>Cargando métricas...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Error al cargar KPIs: {error}</Typography>
      </Box>
    );
  }

  if (!kpis) return null;

  const occupancyData = [
    { name: 'Ocupadas', value: kpis.occupied_spaces, color: '#0057B7' },
    { name: 'Disponibles', value: kpis.available_spaces, color: '#10B981' },
  ];

  const forecastData = [
    { range: '30 días', spaces: kpis.forecast['30d'] || 0 },
    { range: '60 días', spaces: kpis.forecast['60d'] || 0 },
    { range: '90 días', spaces: kpis.forecast['90d'] || 0 },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Activo': return 'success';
      case 'Programado': return 'info';
      case 'Finalizado': return 'default';
      case 'Cancelado': return 'error';
      default: return 'primary';
    }
  };

  const provincialStats = espacios?.reduce((acc, esp) => {
    const p = esp.Provincia || 'Otra';
    if (!acc[p]) acc[p] = { name: p, Ocupadas: 0, Disponibles: 0 };
    if (esp.Estado === 'Disponible') acc[p].Disponibles += 1;
    else acc[p].Ocupadas += 1;
    return acc;
  }, {});
  const provinceChartData = Object.values(provincialStats || {}).sort((a, b) => (b.Ocupadas + b.Disponibles) - (a.Ocupadas + a.Disponibles));

  const typeStats = espacios?.reduce((acc, esp) => {
    const t = esp.Tipo_Espacio || 'Otro';
    if (!acc[t]) acc[t] = { name: t, Ocupadas: 0, Disponibles: 0 };
    if (esp.Estado === 'Disponible') acc[t].Disponibles += 1;
    else acc[t].Ocupadas += 1;
    return acc;
  }, {});
  const typeChartData = Object.values(typeStats || {}).sort((a, b) => (b.Ocupadas + b.Disponibles) - (a.Ocupadas + a.Disponibles));

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#f8fafc' }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} color="primary" sx={{ mb: 0.5 }}>
            📊 Dashboard Ejecutivo
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Resumen de la salud del negocio — Actualizado en tiempo real
          </Typography>
        </Box>

        {/* KPI Cards Row */}
        <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<StorefrontIcon sx={{ fontSize: 28 }} />}
              title="Tasa de Ocupación"
              value={`${kpis.occupancy_rate}%`}
              subtitle={`${kpis.occupied_spaces} de ${kpis.total_spaces} espacios ocupados`}
              gradient="linear-gradient(135deg, #0057B7 0%, #003B7A 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
              title="MRR (Ingresos Mes)"
              value={formatCurrency(kpis.mrr)}
              subtitle="Ingresos recurrentes del mes activo"
              gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<EventAvailableIcon sx={{ fontSize: 28 }} />}
              title="Se Desocupan (30d)"
              value={kpis.forecast['30d'] || 0}
              subtitle="Espacios a vencer en los próximos 30 días"
              gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<PeopleIcon sx={{ fontSize: 28 }} />}
              title="Conversión Leads"
              value={`${kpis.lead_conversion_rate}%`}
              subtitle={`${kpis.converted_clients} contratos de ${kpis.total_leads} leads`}
              gradient="linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
          {/* Revenue Chart */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                💰 Ingresos Mensuales (Últimos 6 Meses)
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={kpis.monthly_revenue} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0057B7" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0057B7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} dx={-10} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1, strokeDasharray: '3 3' }} />
                  <Area type="monotone" name="Ingresos" dataKey="revenue" stroke="#0057B7" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" activeDot={{ r: 6, strokeWidth: 0, fill: '#0057B7' }} />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Occupancy Donut + Forecast */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={3} justifyContent="center">
              {/* Occupancy Donut */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1, textAlign: 'center' }}>
                    🏢 Distribución del Inventario
                  </Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={occupancyData}
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        stroke="none"
                      >
                        {occupancyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Forecast bars */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                    📅 Forecast de Disponibilidad
                  </Typography>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={forecastData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis dataKey="range" type="category" width={70} tickLine={false} axisLine={false} tick={{ fontSize: 12, fontWeight: 600, fill: '#475569' }} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                      <Bar dataKey="spaces" name="Se desocupan" radius={[0, 6, 6, 0]} maxBarSize={24}>
                        {forecastData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={FORECAST_COLORS[index]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Dynamic Inventory Charts */}
        {espacios && espacios.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                  📍 Ocupación por Provincia
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={provinceChartData} margin={{ top: 10, right: 10, left: -20, bottom: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" interval={0} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tick={{ fontSize: 11, fill: '#64748b' }} angle={-45} textAnchor="end" dy={10} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dx={-10} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                    <Legend wrapperStyle={{ bottom: 0 }} iconType="circle" />
                    <Bar dataKey="Ocupadas" stackId="a" fill="#0057B7" maxBarSize={40} />
                    <Bar dataKey="Disponibles" stackId="a" fill="#10B981" maxBarSize={40} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                  🏷️ Ocupación por Tipo de Valla
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={typeChartData} margin={{ top: 10, right: 10, left: -20, bottom: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" interval={0} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tick={{ fontSize: 11, fill: '#64748b' }} angle={-45} textAnchor="end" dy={10} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dx={-10} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                    <Legend wrapperStyle={{ bottom: 0 }} iconType="circle" />
                    <Bar dataKey="Ocupadas" stackId="a" fill="#0057B7" maxBarSize={40} />
                    <Bar dataKey="Disponibles" stackId="a" fill="#10B981" maxBarSize={40} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Recent Contracts Table */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700}>
              📋 Contratos Recientes
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Espacio</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Período</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Monto</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kpis.recent_contracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No hay contratos registrados aún.
                    </TableCell>
                  </TableRow>
                ) : (
                  kpis.recent_contracts.map((c) => (
                    <TableRow key={c.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{c.cliente}</TableCell>
                      <TableCell>{c.codigo_espacio}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{c.fecha_inicio}</Typography>
                        <Typography variant="caption" color="text.secondary">hasta {c.fecha_fin}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#059669' }}>
                        {formatCurrency(c.monto_total)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={c.estado} color={getStatusColor(c.estado)} size="small" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
}
